/**
 * Session management logging utility
 * Provides structured logging for session operations with different levels
 */

const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

const LOG_CATEGORIES = {
  SESSION_INIT: '🔐 Session Init',
  COOKIE_SET: '🍪 Cookie Set',
  COOKIE_GET: '🍪 Cookie Get',
  RESTORATION: '🔄 Restoration',
  NAVIGATION: '🧭 Navigation',
  LOGOUT: '🚪 Logout',
  PERSISTENCE: '💾 Persistence',
  VALIDATION: '✅ Validation'
};

/**
 * Check if logging is enabled for development
 * @returns {boolean} - True if logging should be enabled
 */
const isLoggingEnabled = () => {
  return process.env.NODE_ENV === 'development' || 
         window.location.hostname === 'localhost' ||
         localStorage.getItem('olivs_debug_session') === 'true';
};

/**
 * Format log message with timestamp and context
 * @param {string} category - Log category
 * @param {string} message - Log message
 * @param {Object} context - Additional context data
 * @returns {Array} - Formatted log arguments
 */
const formatLogMessage = (category, message, context = {}) => {
  const timestamp = new Date().toISOString();
  const baseMessage = `${category}: ${message}`;
  
  if (Object.keys(context).length > 0) {
    return [baseMessage, context];
  }
  
  return [baseMessage];
};

/**
 * Generic logging function
 * @param {string} level - Log level (error, warn, info, debug)
 * @param {string} category - Log category
 * @param {string} message - Log message
 * @param {Object} context - Additional context data
 */
const log = (level, category, message, context = {}) => {
  if (!isLoggingEnabled() && level !== LOG_LEVELS.ERROR) {
    return; // Only show errors in production
  }
  
  const args = formatLogMessage(category, message, context);
  
  switch (level) {
    case LOG_LEVELS.ERROR:
      console.error(...args);
      break;
    case LOG_LEVELS.WARN:
      console.warn(...args);
      break;
    case LOG_LEVELS.INFO:
      console.info(...args);
      break;
    case LOG_LEVELS.DEBUG:
      console.debug(...args);
      break;
    default:
      console.log(...args);
  }
};

/**
 * Session initialization logging
 */
export const logSessionInit = {
  success: (context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.SESSION_INIT, 'Initialized successfully', context),
  error: (error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.SESSION_INIT, `Failed: ${error.message}`, { error, ...context }),
  restoration: (restored, context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.SESSION_INIT, `Session restoration: ${restored ? 'success' : 'failed'}`, context)
};

/**
 * Cookie operation logging
 */
export const logCookie = {
  set: (name, success, context = {}) => {
    const level = success ? LOG_LEVELS.DEBUG : LOG_LEVELS.ERROR;
    const message = `Set '${name}': ${success ? 'success' : 'failed'}`;
    log(level, LOG_CATEGORIES.COOKIE_SET, message, context);
  },
  get: (name, found, context = {}) => {
    const level = LOG_LEVELS.DEBUG;
    const message = `Get '${name}': ${found ? 'found' : 'not found'}`;
    log(level, LOG_CATEGORIES.COOKIE_GET, message, context);
  },
  clear: (count, context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.COOKIE_SET, `Cleared ${count} cookies`, context),
  domainError: (domain, error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.COOKIE_SET, `Domain '${domain}' error: ${error.message}`, { error, ...context })
};

/**
 * Session restoration logging
 */
export const logRestoration = {
  attempt: (context = {}) => log(LOG_LEVELS.DEBUG, LOG_CATEGORIES.RESTORATION, 'Attempting session restoration', context),
  success: (context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.RESTORATION, 'Session restored successfully', context),
  failed: (reason, context = {}) => log(LOG_LEVELS.WARN, LOG_CATEGORIES.RESTORATION, `Restoration failed: ${reason}`, context),
  expired: (expirationDate, context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.RESTORATION, `Session expired at ${expirationDate}`, context),
  corrupted: (error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.RESTORATION, `Corrupted session data: ${error.message}`, { error, ...context })
};

/**
 * Navigation logging
 */
export const logNavigation = {
  attempt: (targetUrl, context = {}) => log(LOG_LEVELS.DEBUG, LOG_CATEGORIES.NAVIGATION, `Navigating to: ${targetUrl}`, context),
  success: (targetUrl, context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.NAVIGATION, `Navigation successful: ${targetUrl}`, context),
  failed: (targetUrl, error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.NAVIGATION, `Navigation failed to ${targetUrl}: ${error.message}`, { error, ...context }),
  fallback: (originalUrl, fallbackUrl, context = {}) => log(LOG_LEVELS.WARN, LOG_CATEGORIES.NAVIGATION, `Fallback navigation: ${originalUrl} → ${fallbackUrl}`, context),
  blocked: (targetUrl, reason, context = {}) => log(LOG_LEVELS.WARN, LOG_CATEGORIES.NAVIGATION, `Navigation blocked to ${targetUrl}: ${reason}`, context)
};

/**
 * Logout logging
 */
export const logLogout = {
  attempt: (context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.LOGOUT, 'Logout initiated', context),
  apiSuccess: (context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.LOGOUT, 'API logout successful', context),
  apiFailed: (error, context = {}) => log(LOG_LEVELS.WARN, LOG_CATEGORIES.LOGOUT, `API logout failed: ${error.message}`, { error, ...context }),
  cleanup: (context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.LOGOUT, 'Session cleanup completed', context),
  cleanupError: (error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.LOGOUT, `Cleanup error: ${error.message}`, { error, ...context })
};

/**
 * Persistence logging
 */
export const logPersistence = {
  save: (context = {}) => log(LOG_LEVELS.DEBUG, LOG_CATEGORIES.PERSISTENCE, 'Session saved to localStorage', context),
  saveError: (error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.PERSISTENCE, `Save error: ${error.message}`, { error, ...context }),
  load: (found, context = {}) => log(LOG_LEVELS.DEBUG, LOG_CATEGORIES.PERSISTENCE, `Session load: ${found ? 'found' : 'not found'}`, context),
  loadError: (error, context = {}) => log(LOG_LEVELS.ERROR, LOG_CATEGORIES.PERSISTENCE, `Load error: ${error.message}`, { error, ...context }),
  cleanup: (context = {}) => log(LOG_LEVELS.DEBUG, LOG_CATEGORIES.PERSISTENCE, 'Persistence cleanup completed', context)
};

/**
 * Validation logging
 */
export const logValidation = {
  success: (type, context = {}) => log(LOG_LEVELS.DEBUG, LOG_CATEGORIES.VALIDATION, `${type} validation: passed`, context),
  failed: (type, reason, context = {}) => log(LOG_LEVELS.WARN, LOG_CATEGORIES.VALIDATION, `${type} validation failed: ${reason}`, context),
  expired: (type, expirationDate, context = {}) => log(LOG_LEVELS.INFO, LOG_CATEGORIES.VALIDATION, `${type} expired at ${expirationDate}`, context)
};

/**
 * Browser compatibility logging
 */
export const logCompatibility = {
  cookieSupport: (supported, context = {}) => log(supported ? LOG_LEVELS.DEBUG : LOG_LEVELS.WARN, LOG_CATEGORIES.SESSION_INIT, `Cookie support: ${supported ? 'yes' : 'no'}`, context),
  localStorageSupport: (supported, context = {}) => log(supported ? LOG_LEVELS.DEBUG : LOG_LEVELS.WARN, LOG_CATEGORIES.SESSION_INIT, `localStorage support: ${supported ? 'yes' : 'no'}`, context),
  httpsRequired: (isHttps, context = {}) => {
    if (!isHttps) {
      log(LOG_LEVELS.WARN, LOG_CATEGORIES.SESSION_INIT, 'HTTPS required for secure cookies', context);
    }
  }
};

/**
 * Performance logging
 */
export const logPerformance = {
  navigationTime: (url, duration, context = {}) => {
    const level = duration > 1000 ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
    log(level, LOG_CATEGORIES.NAVIGATION, `Navigation to ${url} took ${duration}ms`, context);
  },
  sessionRestoreTime: (duration, context = {}) => {
    const level = duration > 100 ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
    log(level, LOG_CATEGORIES.RESTORATION, `Session restoration took ${duration}ms`, context);
  }
};

/**
 * Enable debug logging (for development/troubleshooting)
 */
export const enableDebugLogging = () => {
  localStorage.setItem('olivs_debug_session', 'true');
  console.info('🔐 Session Debug: Logging enabled');
};

/**
 * Disable debug logging
 */
export const disableDebugLogging = () => {
  localStorage.removeItem('olivs_debug_session');
  console.info('🔐 Session Debug: Logging disabled');
};

/**
 * Get current logging status
 */
export const getLoggingStatus = () => {
  return {
    enabled: isLoggingEnabled(),
    environment: process.env.NODE_ENV,
    debugMode: localStorage.getItem('olivs_debug_session') === 'true'
  };
};